import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin } from "lucide-react";
import { GroupInfo } from "@/hooks/useUserGroupInfo";
import { formatScheduleDate } from "@/utils/groupScheduleParser";
import { useLanguage } from "@/contexts/LanguageContext";

interface MyGroupViewProps {
  groupInfo: GroupInfo;
}

export function MyGroupView({ groupInfo }: MyGroupViewProps) {
  const { t } = useLanguage();

  return (
    <div className="space-y-6">
      {/* Group Header */}
      <div className="space-y-2">
        <h3 className="text-xl font-semibold">{t('tournament.myGroup')} - {groupInfo.groupName}</h3>
        <p className="text-sm text-muted-foreground">
          {groupInfo.category}
          {groupInfo.subcategory && ` - ${groupInfo.subcategory}`}
        </p>
      </div>

      {/* Participants */}
      <Card className="p-4">
        <h4 className="font-semibold mb-4">{t('tournament.participants')}</h4>
        <div className="space-y-3">
          {groupInfo.participants.map((participant) => (
            <div
              key={participant.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                participant.isUser 
                  ? 'bg-primary/10 border-primary' 
                  : 'bg-muted/30'
              }`}
            >
              <div className="flex items-center gap-3">
                <Badge variant={participant.isUser ? "default" : "secondary"}>
                  {participant.letter}
                </Badge>
                <div>
                  <p className="font-medium">
                    {participant.player1}
                    {participant.player2 && ` / ${participant.player2}`}
                  </p>
                  {participant.isUser && (
                    <p className="text-xs text-muted-foreground">{t('tournament.yourTeam')}</p>
                  )}
                </div>
              </div>
              {participant.isUser && (
                <Badge variant="default" className="ml-2">
                  {t('tournament.you')}
                </Badge>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Matches */}
      {groupInfo.matches.length > 0 && (
        <Card className="p-6">
          <div className="mb-4">
            <h3 className="text-xl font-bold text-primary mb-2">
              {groupInfo.groupName.toUpperCase()}
            </h3>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              {groupInfo.date && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{formatScheduleDate(groupInfo.date)}</span>
                </div>
              )}
              {groupInfo.court && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  <span>{t('tournament.court')} {groupInfo.court}</span>
                </div>
              )}
              {groupInfo.startTime && groupInfo.endTime && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{groupInfo.startTime} - {groupInfo.endTime}</span>
                </div>
              )}
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-primary text-primary-foreground">
                  <th className="p-3 text-left font-semibold">{t('tournament.pair')}</th>
                  {groupInfo.matches.map((_, idx) => (
                    <th key={idx} className="p-3 text-center font-semibold">
                      {idx + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupInfo.participants.map((participant) => (
                  <tr 
                    key={participant.id}
                    className={participant.isUser ? 'bg-primary/10' : ''}
                  >
                    <td className="p-3 border-t">
                      <div className="font-medium">
                        {participant.player1}
                        {participant.player2 && ` / ${participant.player2}`}
                      </div>
                      {participant.isUser && (
                        <Badge variant="default" className="mt-1">{t('tournament.you')}</Badge>
                      )}
                    </td>
                    {groupInfo.matches.map((match) => {
                      const isParticipant = match.participants.includes(participant.letter);
                      return (
                        <td 
                          key={match.id} 
                          className="p-3 border-t text-center"
                        >
                          {isParticipant ? (
                            <div className="text-sm">
                              <div className="font-medium">{match.time}</div>
                              <div className="text-xs text-muted-foreground">
                                {match.court && `${t('tournament.court')} ${match.court}`}
                              </div>
                            </div>
                          ) : (
                            <div className="bg-muted/50 h-12 flex items-center justify-center text-muted-foreground">
                              —
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-center text-sm text-muted-foreground">
            {groupInfo.participants.length} {t('tournament.pairs')} • {groupInfo.matches.length} {t('tournament.matches')}
          </div>
        </Card>
      )}

      {groupInfo.matches.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">
            {t('tournament.matchSchedulesSoon')}
          </p>
        </Card>
      )}
    </div>
  );
}